import { Component, LoadXKTModel, SceneModelEntity } from '@xeokit/xeokit-sdk';

function parseMetadataPreservingLargeIds(text: string) {
  return JSON.parse(text.replace(/:(\s*)(-?\d{16,})(\s*[,}\]])/g, ':$1"$2"$3'));
}

export async function loadModelXKT(
  id: string,
  sources: string[],
  options?: {
    edges?: boolean;
    pbr?: boolean;
    bcfURL?: string;
    metadataURL?: string;
    position?: (number | null | undefined)[];
    rotation?: (number | null | undefined)[];
    scale?: (number | null | undefined)[];
  },
): Promise<string | undefined> {
  const viewerStore = useViewerStore();

  const loader = viewerStore.getLoaders().xktLoaderPlugin;

  if (!viewerStore.isInitialized || !loader) return;

  const t0 = performance.now();

  const loaderOptions: LoadXKTModel & { loadIntoMetaScene?: boolean } = {
    id,
    manifest: {
      xktFiles: sources,
    },
    edges: options?.edges ?? false,
    rotation: [90, 0, 0],
    // excludeUnclassifiedObjects: false,
    //position: [10, 10, 10], // Use to change scene model position
  };

  if (options?.metadataURL) {
    try {
      if (sources.length === 1) {
        const metadata = parseMetadataPreservingLargeIds(
          await $fetch<string>(options.metadataURL, { responseType: 'text' }),
        );

        // XKTLoaderPlugin ignores `manifest` when `metaModelData` is set, so load via `src`
        delete loaderOptions.manifest;
        loaderOptions.src = sources[0];
        loaderOptions.metaModelData = metadata;
      } else {
        loaderOptions.manifest = {
          xktFiles: sources,
          metaModelFiles: [options.metadataURL],
        };
      }

      loaderOptions.loadIntoMetaScene = false;
    } catch (error) {
      console.error(`Failed to load metadata JSON from ${options.metadataURL}`, error);
    }
  }

  const sceneModel = loader.load(loaderOptions);

  return new Promise((resolve, reject) => {
    sceneModel.on('loaded', function () {
      console.log(
        `Model ${id} loaded in ${Math.floor(performance.now() - t0) / 1000.0} seconds, objects: ${Object.keys(sceneModel.objects).length}`,
      );

      const minOpacity = 0.5;

      Object.keys(sceneModel.objects).forEach((objectId) => {
        const object = sceneModel.objects[objectId] as SceneModelEntity;

        if (object.opacity < minOpacity) {
          object.opacity = minOpacity;
        }
      });

      if (options) {
        updateModel(sceneModel, {
          position: options.position,
          // rotation: options.rotation,
          // scale: options.scale,
        });
      }

      if (options?.bcfURL) {
        loadBCFViewpointsFromURL(options.bcfURL);
      }

      viewerStore.saveModelMemento(sceneModel.id);
      resolve(sceneModel.id);
    });

    sceneModel.on('error', function (error) {
      console.error(error);
      reject(error);
    });

    // Losing the WebGL context destroys the viewer mid-load, so 'loaded' would never fire
    (sceneModel as Component).on('destroyed', function () {
      reject(new Error(`Model ${id} was destroyed before it finished loading`));
    });
  });
}

async function loadBCFViewpointsFromURL(bcfURL: string) {
  const viewerBCFViewpointsStore = useViewerBCFViewpointsStore();
  const bcfViewpointsPlugin = viewerBCFViewpointsStore.getPlugin();

  if (bcfViewpointsPlugin) {
    const bcfDataFetch = await $fetch(bcfURL);
    bcfViewpointsPlugin.setViewpoint(bcfDataFetch);
  }
}
